<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTwoFactorEnforcement } from '@/composables/useTwoFactorEnforcement'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

const router = useRouter()
const authStore = useAuthStore()
const { showEnforcementDialog, enforcementMessage, dismissEnforcement } = useTwoFactorEnforcement()

const visible = ref(false)

// Sync with global state
watch(showEnforcementDialog, (newValue) => {
  visible.value = newValue
})

watch(visible, (newValue) => {
  if (!newValue) {
    dismissEnforcement()
  }
})

async function setupTwoFactor() {
  dismissEnforcement()
  // Navigate to 2FA setup page
  await router.push('/account/2fa-required')
}

async function logout() {
  dismissEnforcement()
  await authStore.logout()
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :closable="false"
    :draggable="false"
    modal
    class="two-factor-enforcement-dialog"
    :style="{ width: '450px' }"
  >
    <template #header>
      <div class="dialog-header">
        <i class="pi pi-shield header-icon"></i>
        <span class="header-text">Two-Factor Authentication Required</span>
      </div>
    </template>

    <div class="dialog-content">
      <div class="security-illustration">
        <div class="shield-icon-large">
          <i class="pi pi-lock shield-icon"></i>
        </div>
      </div>

      <p class="enforcement-message">
        {{ enforcementMessage }}
      </p>

      <div class="info-box">
        <i class="pi pi-info-circle"></i>
        <div class="info-text">
          <strong>Why is this required?</strong>
          <p>
            Your organization has enabled mandatory two-factor authentication to protect
            sensitive data. You'll need to set up 2FA before you can continue using the application.
          </p>
        </div>
      </div>

      <div class="requirements-list">
        <h4>You'll need:</h4>
        <ul>
          <li>
            <i class="pi pi-mobile"></i>
            An authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
          </li>
          <li>
            <i class="pi pi-clock"></i>
            About 2 minutes to complete setup
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <Button
          label="Log Out Instead"
          severity="secondary"
          text
          @click="logout"
          class="logout-button"
        />
        <Button
          label="Set Up 2FA Now"
          icon="pi pi-shield"
          @click="setupTwoFactor"
          class="setup-button"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 1.5rem;
  color: #2196f3;
}

.header-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.security-illustration {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.shield-icon-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(33, 150, 243, 0.3);
}

.shield-icon {
  font-size: 2.5rem;
  color: white;
}

.enforcement-message {
  text-align: center;
  font-size: 1rem;
  color: var(--text-color-secondary);
  margin: 0;
  line-height: 1.6;
}

.info-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid var(--primary-color);
}

.info-box i {
  font-size: 1.25rem;
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 2px;
}

.info-text {
  flex: 1;
}

.info-text strong {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color);
}

.info-text p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  line-height: 1.5;
}

.requirements-list {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
}

.requirements-list h4 {
  margin: 0 0 12px 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.requirements-list ul {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.requirements-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.requirements-list li i {
  color: var(--primary-color);
  font-size: 1rem;
  width: 20px;
  flex-shrink: 0;
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.logout-button {
  color: var(--text-color-secondary) !important;
}

.setup-button {
  flex-grow: 1;
  max-width: 200px;
}

/* Responsive adjustments */
@media (max-width: 500px) {
  .dialog-footer {
    flex-direction: column-reverse;
  }

  .setup-button {
    width: 100%;
    max-width: none;
  }

  .logout-button {
    width: 100%;
  }
}
</style>

<style>
/* Global dialog styles */
.two-factor-enforcement-dialog .p-dialog-header {
  padding: 20px 24px 16px;
  border-bottom: none;
}

.two-factor-enforcement-dialog .p-dialog-content {
  padding: 0 24px 24px;
}

.two-factor-enforcement-dialog .p-dialog-footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #e0e0e0;
}
</style>
